"use client"

import BannerWithTracking from "@/components/BannerWithTracking"
import { toImageSrc } from "@/lib/utils"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { Autoplay, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

import "swiper/css"
import "swiper/css/pagination"
import "./promotionBottomBanner.css"

import { ZoomEffectButton } from "@/(admin)/components/button/ZoomEffectButton"
import type { PromotionBottomBannerContentProps } from "@/types/types"
import { usePromotionBottomBanner } from "../hooks/usePromotionBottomBanner"

export const PromotionBottomBannerContent = ({
  id,
  banners = [],
  mobileHeight,
  desktopHeight,
  className = "",
}: PromotionBottomBannerContentProps) => {
  const router = useRouter()
  const { validBanners, hasValidBanners, swiperConfig } = usePromotionBottomBanner({
    banners,
    componentId: id,
  })

  if (!hasValidBanners) {
    return null
  }

  return (
    <div className={`relative w-full h-full ${className}`} id={id}>
      <Swiper
        modules={[Pagination, Autoplay]}
        loop={swiperConfig.loop}
        autoplay={swiperConfig.autoplay}
        pagination={swiperConfig.pagination}
        className="promotion-bottom-banner-swiper w-full h-full"
        style={{
          "--mobile-height": mobileHeight || undefined,
          "--desktop-height": desktopHeight || undefined,
        } as React.CSSProperties & { [key: string]: string | undefined }}
      >
        {validBanners.map((banner, index) => {
          const imageUrl = toImageSrc(banner.image)

          return (
            <SwiperSlide key={index} className="w-full h-full">
              <BannerWithTracking
                bannerId={`promotion-banner-${id}-${index}`}
                bannerUrl={banner.bannerUrl}
                trackingConfig={banner.trackingConfig}
                className="relative w-full banner-image-container"
              >
                {/* Background Image */}
                <Image
                  src={imageUrl}
                  alt={banner.title || `Banner ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="100vw"
                  priority={index === 0}
                />

                {/* Darker overlay for desktop */}
                <div className="absolute inset-0 bg-black/30 sm:bg-black/20 transition-opacity duration-300" />

                {/* Content Overlay */}
                <div className="absolute inset-0 flex items-center justify-start md:justify-center px-4 md:px-0">
                  <div className="w-full sm:w-auto text-center flex flex-col gap-[1.875rem] sm:gap-[3.125rem]">
                    {/* Title */}
                    {banner.title && (
                      <h2 className="text-white text-[1.625rem] sm:text-[3.4375rem] font-extrabold leading-[2.5rem] sm:leading-[5.328125rem]">
                        {banner.title}
                      </h2>
                    )}

                    {/* Button */}
                    {banner.buttonText && banner.buttonLink && (
                      <ZoomEffectButton
                        text={banner.buttonText}
                        onClick={() => router.push(banner.buttonLink!)}
                        height="42px"
                      />
                    )}
                  </div>
                </div>
              </BannerWithTracking>
            </SwiperSlide>
          )
        })}
      </Swiper>
    </div>
  )
}
